/**
 * Knowledge Base articles that offer a "Go to …" CTA into the live UI.
 * `path` may include a `helpGuide` query consumed by the target page.
 */
export type KnowledgeBaseTryIt = {
  path: string;
  /** i18n key under the help namespace (e.g. knowledgeBase.goToCustomizeDashboard). */
  labelKey: string;
};

export const HELP_GUIDE_CUSTOMIZE = "customize";
export const HELP_GUIDE_DASHBOARD_OVERVIEW = "dashboard-overview";
export const HELP_GUIDE_DASHBOARD_KPI = "dashboard-kpi";
export const HELP_GUIDE_DASHBOARD_BRANCHES = "dashboard-branches";
export const HELP_GUIDE_DASHBOARD_CARBON = "dashboard-carbon";
export const HELP_GUIDE_UTILITY_ENTRY = "utility-entry";
/** @deprecated Legacy query value; treated as utility-entry (save step). */
export const HELP_GUIDE_SAVE_UTILITY = "save-utility";
export const HELP_GUIDE_UTILITY_PROOFS = "utility-proofs";
export const HELP_GUIDE_DATA_SOURCE_SETTINGS = "data-source-settings";
export const HELP_GUIDE_DOWNLOAD_REPORT = "download-report";
export const HELP_GUIDE_DOWNLOAD_REPORT_GENERAL = "download-report-general";
export const HELP_GUIDE_DOWNLOAD_REPORT_WASTE = "download-report-waste";
export const HELP_GUIDE_DOWNLOAD_REPORT_CARBON = "download-report-carbon";
export const HELP_GUIDE_METRIC_DEFINITIONS = "metric-definitions";
export const HELP_GUIDE_TRAINING_OVERVIEW = "training-overview";
export const HELP_GUIDE_RECORD_TRAINING = "record-training";
export const HELP_GUIDE_TRAINING_DETAIL = "training-detail";
export const HELP_GUIDE_HSE_WEEKLY = "hse-weekly";
export const HELP_GUIDE_HSE_TERMS = "hse-terms";
export const HELP_GUIDE_CHANGE_LANGUAGE = "change-language";
export const HELP_GUIDE_APPEARANCE = "appearance-theme";

export type HelpDashboardGuideKind =
  | "overview"
  | "kpi"
  | "branches"
  | "carbon";

export function parseHelpDashboardGuide(
  guide: string | null | undefined,
): HelpDashboardGuideKind | null {
  if (!guide) return null;
  if (guide === HELP_GUIDE_DASHBOARD_OVERVIEW) return "overview";
  if (guide === HELP_GUIDE_DASHBOARD_KPI) return "kpi";
  if (guide === HELP_GUIDE_DASHBOARD_BRANCHES) return "branches";
  if (guide === HELP_GUIDE_DASHBOARD_CARBON) return "carbon";
  return null;
}

export type HelpDownloadReportTarget = "all" | "general" | "waste" | "carbon";

export function parseHelpDownloadReportGuide(
  guide: string | null | undefined,
): HelpDownloadReportTarget | null {
  if (!guide) return null;
  if (guide === HELP_GUIDE_DOWNLOAD_REPORT) return "all";
  if (guide === HELP_GUIDE_DOWNLOAD_REPORT_GENERAL) return "general";
  if (guide === HELP_GUIDE_DOWNLOAD_REPORT_WASTE) return "waste";
  if (guide === HELP_GUIDE_DOWNLOAD_REPORT_CARBON) return "carbon";
  return null;
}

export const KNOWLEDGE_BASE_TRY_IT: Record<string, KnowledgeBaseTryIt> = {
  "dashboard-overview": {
    path: `/?helpGuide=${HELP_GUIDE_DASHBOARD_OVERVIEW}`,
    labelKey: "knowledgeBase.goToDashboard",
  },
  "dashboard-widget-customization": {
    path: `/?helpGuide=${HELP_GUIDE_CUSTOMIZE}`,
    labelKey: "knowledgeBase.goToCustomizeDashboard",
  },
  "dashboard-kpi-limits": {
    path: `/?helpGuide=${HELP_GUIDE_DASHBOARD_KPI}`,
    labelKey: "knowledgeBase.goToKpiLimits",
  },
  "dashboard-branches-compare": {
    path: `/?helpGuide=${HELP_GUIDE_DASHBOARD_BRANCHES}`,
    labelKey: "knowledgeBase.goToBranchesCompare",
  },
  "dashboard-carbon-avoidance": {
    path: `/?helpGuide=${HELP_GUIDE_DASHBOARD_CARBON}`,
    labelKey: "knowledgeBase.goToCarbonAvoidanceTab",
  },
  "utility-data-entry": {
    path: `/data-entry?helpGuide=${HELP_GUIDE_UTILITY_ENTRY}`,
    labelKey: "knowledgeBase.goToDataEntry",
  },
  "utility-proofs": {
    path: `/data-entry?helpGuide=${HELP_GUIDE_UTILITY_PROOFS}`,
    labelKey: "knowledgeBase.goToUtilityProofs",
  },
  "data-source-settings": {
    path: `/data-entry?helpGuide=${HELP_GUIDE_DATA_SOURCE_SETTINGS}`,
    labelKey: "knowledgeBase.goToDataSourceSettings",
  },
  "esg-reports": {
    path: `/esg-reports?helpGuide=${HELP_GUIDE_DOWNLOAD_REPORT}`,
    labelKey: "knowledgeBase.goToDownloadReport",
  },
  "report-general-esg": {
    path: `/esg-reports?helpGuide=${HELP_GUIDE_DOWNLOAD_REPORT_GENERAL}`,
    labelKey: "knowledgeBase.goToGeneralReport",
  },
  "report-waste-circularity": {
    path: `/esg-reports?helpGuide=${HELP_GUIDE_DOWNLOAD_REPORT_WASTE}`,
    labelKey: "knowledgeBase.goToWasteReport",
  },
  "report-carbon-avoidance": {
    path: `/esg-reports?helpGuide=${HELP_GUIDE_DOWNLOAD_REPORT_CARBON}`,
    labelKey: "knowledgeBase.goToCarbonReport",
  },
  "report-metric-definitions": {
    path: `/esg-reports?helpGuide=${HELP_GUIDE_METRIC_DEFINITIONS}`,
    labelKey: "knowledgeBase.goToMetricDefinitions",
  },
  "employee-training-overview": {
    path: `/employee-training?helpGuide=${HELP_GUIDE_TRAINING_OVERVIEW}`,
    labelKey: "knowledgeBase.goToTraining",
  },
  "record-training-session": {
    path: `/employee-training?helpGuide=${HELP_GUIDE_RECORD_TRAINING}`,
    labelKey: "knowledgeBase.goToRecordTraining",
  },
  "employee-training-detail": {
    path: `/employee-training?helpGuide=${HELP_GUIDE_TRAINING_DETAIL}`,
    labelKey: "knowledgeBase.goToTrainingDetail",
  },
  "hse-weekly-report": {
    path: `/hse?helpGuide=${HELP_GUIDE_HSE_WEEKLY}`,
    labelKey: "knowledgeBase.goToHse",
  },
  "hse-terms": {
    path: `/hse?helpGuide=${HELP_GUIDE_HSE_TERMS}`,
    labelKey: "knowledgeBase.goToHseTerms",
  },
  "change-language": {
    path: `/?helpGuide=${HELP_GUIDE_CHANGE_LANGUAGE}`,
    labelKey: "knowledgeBase.goToChangeLanguage",
  },
  "appearance-theme": {
    path: `/?helpGuide=${HELP_GUIDE_APPEARANCE}`,
    labelKey: "knowledgeBase.goToAppearance",
  },
};
